import React from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Button,
  Divider, 
  List, 
  ListItem, 
  ListItemText,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import { theme } from '../../../../theme';
import InfoIcon from '@mui/icons-material/Info';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import PauseIcon from '@mui/icons-material/Pause';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import { IconButton } from '@mui/material';
import { getDisplayDay, normalizeDayName } from '../../../../utils/dayUtils';
import { useSchedulesBreaksByTeamMemberRetrieveQuery } from '../../../../services/GLOWClient';

/**
 * DetailSchedule component for displaying schedule details in a dialog
 * 
 * @param {Object} props
 * @param {Object} props.schedule - The schedule data to display
 * @param {Array} props.breaks - The breaks data for this schedule
 * @param {boolean} props.open - External control for dialog open state
 * @param {function} props.onClose - External close handler
 * @returns {React.ReactElement}
 */
const DetailSchedule = ({ schedule, breaks = [], open, onClose }) => {
  // Compute team member id safely before hooks
  const teamMemberId = schedule?.team_member?.team_member_id;

  // Fetch breaks like in updateSchedule dialog (source of truth)
  const { data: fetchedBreaks } = useSchedulesBreaksByTeamMemberRetrieveQuery(
    teamMemberId ? { teamMemberId } : undefined,
    { skip: !teamMemberId, refetchOnMountOrArgChange: true }
  );

  // Bail out after hooks to satisfy rules-of-hooks
  if (!schedule) return null;

  // Ensure breaks is always an array
  const breaksArray = Array.isArray(breaks) ? breaks : [breaks].filter(Boolean);

  const fetchedArray = Array.isArray(fetchedBreaks)
    ? fetchedBreaks
    : fetchedBreaks
    ? [fetchedBreaks]
    : [];

  // Prefer fetched breaks; fallback to passed-in breaks
  const sourceBreaks = fetchedArray.length > 0 ? fetchedArray : breaksArray;

  // Filter to the same day as the schedule (normalize names for safety)
  const dayFilteredBreaks = sourceBreaks.filter(
    (b) => normalizeDayName(b?.day_of_week || '') === normalizeDayName(schedule?.day_of_week || '')
  );

  const {
    team_member,
    salon,
    day_of_week,
    start_time,
    end_time
  } = schedule;

  // Format time strings for better display
  const formatTime = (timeString) => {
    if (!timeString) return '';
    // If the time already has the format HH:MM:SS, extract just HH:MM
    if (timeString.includes(':')) {
      return timeString.substring(0, 5);
    }
    return timeString;
  };

  // Reusable section component
  const DetailSection = ({ title, icon, children, sx = {} }) => (
    <Card 
      elevation={0} 
      sx={{ 
        mb: 3, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: theme.colors.primary[400],
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        },
        ...sx 
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
          <Avatar 
            sx={{ 
              bgcolor: theme.colors.primary[500], 
              width: 32, 
              height: 32, 
              mr: 1.5,
              '& .MuiSvgIcon-root': { fontSize: '1.1rem' }
            }}
          >
            {icon}
          </Avatar>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.01em'
            }}
          >
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  // Field component for consistent styling
  const DetailField = ({ label, value, xs = 12, sm = 6 }) => (
    <Grid item xs={xs} sm={sm}>
      <Box sx={{ mb: 1.5 }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'text.secondary',
            mb: 0.5,
            display: 'block'
          }}
        >
          {label}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 500,
            color: 'text.primary',
            lineHeight: 1.4
          }}
        >
          {value || 'N/A'}
        </Typography>
      </Box>
    </Grid>
  );

  // Content for the dialog
  const dialogContent = (
    <Box sx={{ pt: 1 }}>
      {/* Day and Time - Featured Section */}
      <DetailSection 
        title="Horaires" 
        icon={<AccessTimeIcon />}
        sx={{ 
          background: `linear-gradient(135deg, ${theme.colors.primary[100]}40 0%, ${theme.colors.primary[50]}80 100%)`,
          borderColor: theme.colors.primary[300]
        }}
      >
        <Grid container spacing={3}>
          <DetailField 
            label="Jour" 
            value={getDisplayDay(day_of_week)} 
            xs={12} 
            sm={4}
          />
          <DetailField 
            label="Heure de début" 
            value={formatTime(start_time)} 
            xs={6} 
            sm={4}
          />
          <DetailField 
            label="Heure de fin" 
            value={formatTime(end_time)} 
            xs={6} 
            sm={4}
          />
        </Grid>
      </DetailSection>

      {/* Salon Information */}
      <DetailSection title="Information du salon" icon={<BusinessIcon />}>
        <Grid container spacing={3}>
          <DetailField 
            label="Nom du salon" 
            value={salon?.name} 
            xs={12} 
            sm={6}
          />
          <DetailField 
            label="Téléphone" 
            value={salon?.phone} 
            xs={12} 
            sm={6}
          />
          <DetailField 
            label="Adresse" 
            value={salon?.address} 
            xs={12} 
            sm={6}
          />
          <DetailField 
            label="Email" 
            value={salon?.email} 
            xs={12} 
            sm={6}
          />
        </Grid>
      </DetailSection>

      {/* Team Information */}
      <DetailSection title="Information de l'équipe" icon={<GroupIcon />}>
        <Grid container spacing={3}>
          <DetailField 
            label="Nom de l'équipe" 
            value={team_member?.team_name} 
            xs={12} 
            sm={6}
          />
          <DetailField 
            label="Employé" 
            value={team_member?.user_details?.full_name} 
            xs={12} 
            sm={6}
          />
        </Grid>
      </DetailSection>

      {/* Services */}
      <DetailSection title="Services" icon={<RoomServiceIcon />}>
        {team_member?.service_names?.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {team_member.service_names.map((service) => (
              <Chip 
                key={service.service_id} 
                label={service.name} 
                variant="outlined"
                size="medium"
                sx={{ 
                  borderRadius: '20px',
                  fontWeight: 500,
                  borderColor: theme.colors.secondary[300],
                  '&:hover': {
                    backgroundColor: theme.colors.primary[500],
                    borderColor: theme.colors.primary[600],
                    color: 'white'
                  }
                }}
              />
            ))}
          </Box>
        ) : (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 3,
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          >
            <RoomServiceIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">Aucun service assigné</Typography>
          </Box>
        )}
      </DetailSection>

      {/* Breaks */}
      <DetailSection title="Pauses" icon={<PauseIcon />}>
        {dayFilteredBreaks && dayFilteredBreaks.length > 0 ? (
          <TableContainer 
            sx={{ 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      color: 'text.secondary'
                    }}
                  >
                    Début
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      color: 'text.secondary'
                    }}
                  >
                    Fin
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dayFilteredBreaks.map((breakItem, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'action.hover' 
                      },
                      '&:last-child td': { 
                        border: 0 
                      }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {formatTime(breakItem.start_time)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {formatTime(breakItem.end_time)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 3,
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          >
            <PauseIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">Aucune pause programmée</Typography>
          </Box>
        )}
      </DetailSection>
    </Box>
  );

  // If external control is provided, use it; otherwise use internal trigger
  if (open !== undefined) {
    return (
      <MuiDialog
        open={open}
        icon={<InfoIcon />}
        triggerButton={<Button style={{ display: 'none' }}>Hidden</Button>}
        title="Détails du planning"
        subtitle={`${getDisplayDay(day_of_week)} - ${formatTime(start_time)} à ${formatTime(end_time)}`}
        content={dialogContent}
        maxWidth="md"
        onExternalClose={onClose}
      />
    );
  }

  // Default behavior with internal trigger button
  return (
    <MuiDialog
      icon={<InfoIcon />}
      triggerButton={
        <IconButton 
          size="small" 
          color="primary" 
          aria-label="Voir les détails du planning"
          sx={{
            color: theme.colors.primary[500],
            '&:hover': {
              backgroundColor: theme.colors.primary[100],
              color: theme.colors.primary[700]
            }
          }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      }
      title="Détails du planning"
      subtitle={`${getDisplayDay(day_of_week)} - ${formatTime(start_time)} à ${formatTime(end_time)}`}
      content={dialogContent}
      maxWidth="md"
    />
  );
};

export default DetailSchedule;